import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, CalendarDays, CheckCircle2, AlertTriangle, Upload, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import CsvImportDialog from "@/components/admin/CsvImportDialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

function formatDate(iso) {
    try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
}

function CompatBadge({ compat, distance_km }) {
    const map = {
        compatible: { cls: "bg-green-100 text-green-700", icon: <CheckCircle2 className="h-3 w-3" />, label: "Compatible" },
        hors_zone: { cls: "bg-orange-100 text-brand-orange", icon: <AlertTriangle className="h-3 w-3" />, label: `Hors zone${distance_km ? ` · ${distance_km}km` : ""}` },
        hors_dispos: { cls: "bg-yellow-100 text-yellow-700", icon: <AlertTriangle className="h-3 w-3" />, label: "Pas dispo ce jour" },
        both_issues: { cls: "bg-gray-100 text-gray-600", icon: <AlertTriangle className="h-3 w-3" />, label: "Hors zone + hors dispo" },
    };
    const c = map[compat] || map.both_issues;
    return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${c.cls}`}>{c.icon} {c.label}</span>;
}

function SessionDialog({ open, initial, formations, salles, onClose, onSaved }) {
    const isEdit = !!initial?.id;
    const empty = {
        formation_slug: formations?.[0]?.slug || "",
        start_date: "",
        end_date: "",
        start_time: "09:00",
        end_time: "17:00",
        salle_id: salles?.[0]?.id || "",
        formateur_id: null,
        total_seats: 12,
        status: "open",
    };
    const [f, setF] = useState(initial || empty);
    const [compat, setCompat] = useState([]);
    const [loadingCompat, setLoadingCompat] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => { setF(initial || empty); /* eslint-disable-next-line */ }, [initial, open]);

    useEffect(() => {
        if (!open || !f.salle_id || !f.start_date) { setCompat([]); return; }
        setLoadingCompat(true);
        api.get("/admin/sessions/compatible-formateurs", { params: { salle_id: f.salle_id, date: f.start_date } })
            .then((r) => setCompat(r.data))
            .catch(() => setCompat([]))
            .finally(() => setLoadingCompat(false));
    }, [f.salle_id, f.start_date, open]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...f, end_date: f.end_date || f.start_date };
            if (isEdit) await api.patch(`/admin/sessions/${initial.id}`, payload);
            else await api.post("/admin/sessions", payload);
            toast.success(isEdit ? "Session mise à jour" : "Session créée");
            onSaved(); onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-brand-ink/50 p-4" data-testid="session-dialog">
            <div className="relative mx-auto mt-6 w-full max-w-3xl rounded-3xl bg-white p-8 shadow-premium-lg">
                <button onClick={onClose} className="absolute right-4 top-4 text-brand-smoke hover:text-brand-ink"><X className="h-5 w-5" /></button>
                <h3 className="font-display text-3xl">{isEdit ? "Modifier la session" : "Nouvelle session"}</h3>
                <form onSubmit={submit} className="mt-6 space-y-5">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Formation</label>
                        <select required value={f.formation_slug} onChange={(e) => setF({ ...f, formation_slug: e.target.value })} data-testid="session-field-formation" className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3">
                            {formations.map((fo) => <option key={fo.slug} value={fo.slug}>{fo.name}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider">Date</label>
                            <input type="date" required value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="session-field-date" className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider">Début</label>
                            <input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider">Fin</label>
                            <input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider">Salle</label>
                            <select required value={f.salle_id} onChange={(e) => setF({ ...f, salle_id: e.target.value })} data-testid="session-field-salle" className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3">
                                <option value="">— Sélectionner —</option>
                                {salles.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.city}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider">Places (total)</label>
                            <input type="number" value={f.total_seats} onChange={(e) => setF({ ...f, total_seats: parseInt(e.target.value || 12) })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3" />
                        </div>
                    </div>

                    {/* Compatible formateurs */}
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Formateur (matching auto)</label>
                        {loadingCompat ? (
                            <div className="mt-2 rounded-xl border border-brand-line p-4 text-sm text-brand-smoke">Calcul des compatibilités…</div>
                        ) : !f.salle_id || !f.start_date ? (
                            <div className="mt-2 rounded-xl border border-dashed border-brand-line p-4 text-sm text-brand-smoke">Sélectionnez une salle + une date pour voir les formateurs compatibles.</div>
                        ) : (
                            <div className="mt-2 max-h-72 overflow-auto rounded-xl border border-brand-line" data-testid="session-compat-list">
                                <label className="flex items-center gap-3 border-b border-brand-line bg-[#FAFAFA] px-4 py-2.5 text-sm">
                                    <input type="radio" name="formateur" checked={!f.formateur_id} onChange={() => setF({ ...f, formateur_id: null })} className="h-4 w-4 accent-brand-orange" />
                                    <span className="text-brand-smoke">Aucun (à définir plus tard)</span>
                                </label>
                                {compat.map((fm) => (
                                    <label key={fm.id} className={`flex items-center justify-between gap-3 border-b border-brand-line px-4 py-2.5 text-sm hover:bg-[#FAFAFA] ${fm.compatibility === "compatible" ? "bg-green-50" : ""}`}>
                                        <span className="flex items-center gap-3">
                                            <input type="radio" name="formateur" checked={f.formateur_id === fm.id} onChange={() => setF({ ...f, formateur_id: fm.id })} data-testid={`session-formateur-${fm.id}`} className="h-4 w-4 accent-brand-orange" />
                                            <span>
                                                <strong>{fm.first_name} {fm.last_name}</strong>
                                                <span className="ml-2 text-xs text-brand-smoke">{fm.email}</span>
                                            </span>
                                        </span>
                                        <CompatBadge compat={fm.compatibility} distance_km={fm.distance_km} />
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Statut</label>
                        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3">
                            <option value="open">Ouverte</option>
                            <option value="closed">Fermée</option>
                            <option value="full">Complète</option>
                        </select>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm hover:bg-brand-line/50">Annuler</button>
                        <button type="submit" disabled={saving} data-testid="session-dialog-submit" className="rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60">
                            {saving ? "Enregistrement…" : (isEdit ? "Enregistrer" : "Créer")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function AdminSessions() {
    const [items, setItems] = useState([]);
    const [formations, setFormations] = useState([]);
    const [salles, setSalles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState({ open: false, initial: null });
    const [importOpen, setImportOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [s, f, sl] = await Promise.all([
                api.get("/admin/sessions"),
                api.get("/formations"),
                api.get("/admin/salles"),
            ]);
            setItems(s.data);
            setFormations(f.data);
            setSalles(sl.data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const remove = async (s) => {
        if (!window.confirm("Supprimer cette session ?")) return;
        try { await api.delete(`/admin/sessions/${s.id}`); toast.success("Session supprimée"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-admin-sessions">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="font-display text-5xl font-normal tracking-tightest">Sessions</h1>
                    <p className="mt-2 text-brand-smoke">Planifiez les sessions et assignez vos formateurs avec le matching automatique.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setImportOpen(true)}
                        data-testid="admin-sessions-import-csv"
                        className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2.5 text-sm font-medium hover:border-brand-orange hover:text-brand-orange"
                    >
                        <Upload className="h-3.5 w-3.5" /> CSV
                    </button>
                    <Link
                        to="/admin/sessions/nouvelle"
                        data-testid="admin-sessions-wizard"
                        className={`inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-3 text-sm font-semibold text-white hover:bg-[#d9772c] ${salles.length === 0 ? "pointer-events-none opacity-50" : ""}`}
                    >
                        <Sparkles className="h-4 w-4" /> Nouvelle session (wizard)
                    </Link>
                    <button
                        onClick={() => setDialog({ open: true, initial: null })}
                        data-testid="admin-sessions-new"
                        disabled={salles.length === 0}
                        title={salles.length === 0 ? "Créez d'abord au moins une salle" : ""}
                        className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2.5 text-sm font-medium hover:border-brand-orange hover:text-brand-orange disabled:opacity-50"
                    >
                        <Plus className="h-3.5 w-3.5" /> Form rapide
                    </button>
                </div>
            </div>
            {salles.length === 0 && !loading && (
                <div className="mt-6 rounded-2xl border border-brand-orange/40 bg-brand-orange/5 p-4 text-sm text-brand-ink/85">
                    💡 Créez d'abord au moins une <strong>salle</strong> dans Admin &gt; Salles avant de planifier des sessions.
                </div>
            )}

            <div className="mt-8 overflow-hidden rounded-2xl border border-brand-line bg-white shadow-premium">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-brand-line bg-[#FAFAFA] text-left text-xs uppercase tracking-wider text-brand-smoke">
                            <th className="px-6 py-4">Formation</th>
                            <th className="px-6 py-4">Date</th>
                            <th className="px-6 py-4">Lieu</th>
                            <th className="px-6 py-4">Formateur</th>
                            <th className="px-6 py-4">Places</th>
                            <th className="px-6 py-4">Statut</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-line">
                        {loading ? (
                            <tr><td colSpan={7} className="px-6 py-10 text-center text-brand-smoke">Chargement…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={7} className="px-6 py-10 text-center text-brand-smoke">Aucune session.</td></tr>
                        ) : (
                            items.map((s) => {
                                const booked = s.total_seats - s.available_seats;
                                return (
                                    <tr key={s.id} className="hover:bg-[#FAFAFA]" data-testid={`session-row-${s.id}`}>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange"><CalendarDays className="h-4 w-4" /></div>
                                                <strong>{s.formation_name}</strong>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">{formatDate(s.start_date)} <span className="text-brand-smoke">· {s.start_time}–{s.end_time}</span></td>
                                        <td className="px-6 py-4">{s.venue_name}<div className="text-xs text-brand-smoke">{s.city}</div></td>
                                        <td className="px-6 py-4">{s.trainer_name || <span className="text-xs italic text-brand-smoke">À définir</span>}</td>
                                        <td className="px-6 py-4"><strong>{booked}/{s.total_seats}</strong></td>
                                        <td className="px-6 py-4">
                                            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase ${s.status === "open" ? "bg-green-100 text-green-700" : s.status === "full" ? "bg-orange-100 text-brand-orange" : "bg-gray-100 text-gray-600"}`}>
                                                {s.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="inline-flex gap-2">
                                                <button onClick={() => setDialog({ open: true, initial: { ...s } })} data-testid={`session-edit-${s.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange"><Pencil className="h-3.5 w-3.5" /></button>
                                                <button onClick={() => remove(s)} data-testid={`session-delete-${s.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <SessionDialog open={dialog.open} initial={dialog.initial} formations={formations} salles={salles} onClose={() => setDialog({ open: false, initial: null })} onSaved={load} />
            <CsvImportDialog kind="sessions" open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
        </div>
    );
}
